import React, { useState, useEffect } from 'react'
import { Link, useSearch } from '@tanstack/react-router'
import {
  Search,
  PackageCheck,
  AlertCircle,
  ArrowLeft,
  LogIn,
} from 'lucide-react'
import { apiGet } from '../lib/api'
import { useAuth } from '../context/AuthContext'
import { StorefrontLayout } from '../layouts/StorefrontLayout'
import { OrderTimeline, type OrderTrackingData } from '../components/OrderTimeline'

export const TrackOrderPage: React.FC = () => {
  const { id: initialId } = useSearch({ from: '/track' })
  const { isCustomer } = useAuth()
  const [mode, setMode] = useState<'account' | 'id'>('account')
  const [trackingIdInput, setTrackingIdInput] = useState(initialId || '')
  const [data, setData] = useState<OrderTrackingData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const fetchByTrackingId = async (idToFetch: string) => {
    const trimmed = idToFetch.trim()
    if (!trimmed) {
      setError('Please enter your tracking ID.')
      return
    }
    setLoading(true)
    setError('')
    setData(null)
    try {
      const res = await apiGet<OrderTrackingData>(`orders/track/${encodeURIComponent(trimmed)}`)
      setData(res)
    } catch (err: unknown) {
      setError((err as Error).message || 'Tracking ID not found.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (initialId) {
      setMode('id')
      fetchByTrackingId(initialId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialId])

  return (
    <StorefrontLayout>
      <div className="max-w-2xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-[var(--text-muted)] hover:text-[var(--gold-main)] mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to store
        </Link>

        <div className="glass-luxury-card p-6 sm:p-8 space-y-6">
          <div className="flex items-center gap-2">
            <PackageCheck className="w-6 h-6 text-[var(--gold-main)]" />
            <h1 className="text-xl font-title font-bold">Track your orders</h1>
          </div>

          <div className="flex gap-2 p-1 bg-[var(--bg-surface-2)] rounded-xl border border-[var(--border)]">
            <button
              type="button"
              onClick={() => { setMode('account'); setError(''); setData(null) }}
              className={`flex-1 py-2 text-xs rounded-lg font-semibold transition ${
                mode === 'account' ? 'bg-[var(--gold-main)] text-[var(--gold-contrast)]' : 'text-[var(--text-muted)]'
              }`}
            >
              My Orders
            </button>
            <button
              type="button"
              onClick={() => { setMode('id'); setError('') }}
              className={`flex-1 py-2 text-xs rounded-lg font-semibold transition ${
                mode === 'id' ? 'bg-[var(--gold-main)] text-[var(--gold-contrast)]' : 'text-[var(--text-muted)]'
              }`}
            >
              <Search className="w-3.5 h-3.5 inline mr-1" />
              Tracking ID
            </button>
          </div>

          {mode === 'account' ? (
            <div className="space-y-4">
              {isCustomer ? (
                <div className="text-center space-y-4 py-4">
                  <p className="text-xs text-[var(--text-muted)]">
                    View all orders linked to your account with full status and timeline.
                  </p>
                  <Link to="/account/orders" className="btn-gold py-2.5 px-6 text-sm inline-flex items-center gap-2">
                    <PackageCheck className="w-4 h-4" />
                    Go to My Orders
                  </Link>
                </div>
              ) : (
                <div className="rounded-xl border border-[var(--border)] p-6 text-center space-y-4 bg-[var(--bg-surface-2)]">
                  <p className="text-xs text-[var(--text-muted)]">
                    Sign in to your customer account to see all your orders and their status.
                  </p>
                  <Link
                    to="/account/login"
                    search={{ redirect: '/account/orders' }}
                    className="btn-gold py-2.5 px-6 text-sm inline-flex items-center gap-2"
                  >
                    <LogIn className="w-4 h-4" />
                    Sign in
                  </Link>
                  <p className="text-[10px] text-[var(--text-faint)]">
                    New customer?{' '}
                    <Link to="/account/register" search={{ redirect: '/account/orders' }} className="text-[var(--gold-main)] hover:underline">
                      Create an account
                    </Link>
                  </p>
                </div>
              )}
            </div>
          ) : (
            <form
              onSubmit={(e) => { e.preventDefault(); fetchByTrackingId(trackingIdInput) }}
              className="space-y-2"
            >
              <label className="form-label" htmlFor="tracking-id">Tracking ID *</label>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  id="tracking-id"
                  type="text"
                  value={trackingIdInput}
                  onChange={(e) => setTrackingIdInput(e.target.value)}
                  placeholder="e.g. BC20260001TRK"
                  className="form-input font-mono text-sm flex-1"
                />
                <button type="submit" disabled={loading} className="btn-gold py-2.5 px-6 text-sm shrink-0">
                  {loading ? 'Searching…' : 'Track'}
                </button>
              </div>
              <p className="text-[10px] text-[var(--text-faint)]">
                No login needed — use the tracking ID from your order confirmation.
              </p>
            </form>
          )}

          {error && (
            <div className="alert alert-error flex items-center gap-2 text-xs">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          {data && <OrderTimeline data={data} />}
        </div>
      </div>
    </StorefrontLayout>
  )
}
